import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Styled Components
import {
  ActionButton,
  ButtonRow,
  Container,
  ContainerLoader,
  ContainerText,
  ModalContainerStyled,
  PhotoName,
  Title,
} from './styles';

// Types
import { ModalProps } from './types';

// Components
import { Loader } from '../../../inputs/Loader';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

/**
 * Confirmación antes de borrar una foto de un tramo.
 *
 * Borrar una foto ya subida es irreversible y es EVIDENCIA de una intervención,
 * así que no puede colgar de un solo toque en una tablet que se maneja con
 * guantes.
 *
 * Copia la estructura de `DeleteExternalCompanyModal` — el canónico vigente de
 * la familia de confirmaciones destructivas: `AlertIcon` (el componente, no el
 * `alert-icon.svg` granate de los modales viejos), bloque de texto agrupado y
 * fila de botones centrada. El icono se centra solo con el `align-items` de la
 * columna.
 *
 * Sin claves i18n nuevas: la pregunta genérica ya existe
 * (`common:messages.confirmDelete`).
 */
export const DeletePhotoModal = ({
  onClickClose,
  photoName,
  onConfirmDelete,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onConfirmDelete();
      onClickClose();
    } catch {
      // El error de negocio lo canta el hook que borra; aquí solo se devuelve
      // el control para que se pueda reintentar o cancelar.
      setIsLoading(false);
    }
  };

  return (
    <ModalContainerStyled
      width={50}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      {isLoading && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <Container
        {...props}
        role="dialog"
        aria-modal="true"
        aria-label={t('messages.confirmDelete')}
        isLoading={isLoading}
      >
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">{t('messages.confirmDelete')}</Title>
          <PhotoName variant="semiBold">{photoName}</PhotoName>
        </ContainerText>

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isLoading}
          />
          <ActionButton
            type="button"
            data-testid="confirm-delete-photo"
            text={isLoading ? t('buttons.deleting') : t('buttons.delete')}
            onClick={handleDelete}
            disabled={isLoading}
          />
        </ButtonRow>
      </Container>
    </ModalContainerStyled>
  );
};

export default DeletePhotoModal;
